import React, { useCallback, useContext, useEffect } from "react";
import { createContext, ReactNode, useState } from "react";
import { CodeScannerOptionsProvider } from "../../../component/code-scanner/code-scanner-settings-context";
import { CodeScanner, CodeScannerFormat } from "../../../component/code-scanner/code-scanner";

interface ICodeScannerContext {
    onCodeScanned: ((code: string) => void) | undefined;
    attachCodeScanSubscription: (fn: (code: string) => void, withBarcode: boolean, withQr: boolean) => void;
    pauseCodeScanSubscription: () => void;
    restartCodeScanSubscription: () => void;
    detachCodeScanner: () => void;
    attachKeyboardCodeScanSubscription: (fn: (code: string) => void) => void;
    detachKeyboardCodeScanner: () => void;
}

const CodeScannerContext = createContext<ICodeScannerContext>({
    onCodeScanned: undefined,
    attachCodeScanSubscription: () => ({}),
    pauseCodeScanSubscription: () => ({}),
    restartCodeScanSubscription: () => ({}),
    detachCodeScanner: () => ({}),
    attachKeyboardCodeScanSubscription: () => ({}),
    detachKeyboardCodeScanner: () => ({}),
});

export const CodeScannerContextProvider = ({ children }: { children: ReactNode }) => {
    const [subscription, setSubscription] = useState<((code: string) => void) | undefined>(undefined);
    const [formats, setFormats] = useState<CodeScannerFormat[]>([CodeScannerFormat.BARCODE]);
    const [isPaused, setPause] = useState(false);
    const [keyboardSubscription, setKeyboardSubscription] = useState<((code: string) => void) | undefined>(undefined);

    const handleAttachCodeScanner = useCallback((fn: (code: string) => void,
                                                 withBarcode: boolean, withQr: boolean) => {
        setSubscription((code: string) => fn(code));
        setFormats([
            ...(withBarcode ? [CodeScannerFormat.BARCODE] : []),
            ...(withQr ? [CodeScannerFormat.QR_CODE] : []),
        ]);
        setPause(false);
    }, []);

    const handleDetachCodeScanner = useCallback(() => {
        setSubscription(undefined);
        setPause(false);
    }, []);

    const handleSetKeyboardSubscription = useCallback((fn: (code: string) => void) => {
        setKeyboardSubscription((code: string) => fn(code));
    }, []);

    return <CodeScannerContext.Provider value={{
        onCodeScanned: !isPaused ? subscription : undefined,
        attachCodeScanSubscription: handleAttachCodeScanner,
        pauseCodeScanSubscription: () => setPause(true),
        restartCodeScanSubscription: () => setPause(false),
        detachCodeScanner: handleDetachCodeScanner,
        attachKeyboardCodeScanSubscription: handleSetKeyboardSubscription,
        detachKeyboardCodeScanner: () => setKeyboardSubscription(undefined),
    }}>
        <CodeScannerOptionsProvider>
            <CodeScanner formats={formats} onValueScanned={!isPaused ? subscription : undefined}
                         onKeyboardValueScanned={keyboardSubscription} />
        </CodeScannerOptionsProvider>
        {children}
    </CodeScannerContext.Provider>;
};

export const useScannerContext = () => {
    return useContext(CodeScannerContext);
};

const useCustomScanner = (fn: ((code: string) => void), withBarcode: boolean, withQr: boolean) => {
    const { attachCodeScanSubscription, detachCodeScanner } =  useContext(CodeScannerContext);

    return useEffect(() => {
        attachCodeScanSubscription(() => (value: string) => fn(value), withBarcode, withQr);

        return () => {
            detachCodeScanner();
        };
    }, []);
};

export const useScanner = (fn: ((code: string) => void)) => {
    return useCustomScanner(fn, true, true);
};

export const useBarcodeScanner = (fn: ((code: string) => void)) => {
    return useCustomScanner(fn, true, false);
};

export const useQrCodeScanner = (fn: ((code: string) => void)) => {
    return useCustomScanner(fn, false, true);
};

export const useKeyboardScanner = (fn: ((code: string) => void)) => {
    const { attachKeyboardCodeScanSubscription, detachKeyboardCodeScanner } =  useContext(CodeScannerContext);

    return useEffect(() => {
        attachKeyboardCodeScanSubscription(() => (value: string) => fn(value));

        return () => {
            detachKeyboardCodeScanner();
        };
    }, []);
};
